import { useCallback, useState } from 'react';

import {
    FadeCoverGallery,
    type FadeCoverSlide,
} from '@/components/site/fade-cover-gallery';
import type { ContentImage } from '@/lib/content-types';

/**
 * Article hero: single cover, or a fading gallery with arrows + dots when
 * multiple main images are set.
 */
export function ArticleHeroGallery({
    images,
    title,
}: {
    images: ContentImage[];
    title: string;
}) {
    const slides = images.flatMap((img): FadeCoverSlide[] => {
        const src = img.coverUrl ?? img.url;
        if (!src) return [];
        return [
            {
                id: img.$id,
                src,
                alt: img.alt || img.caption || title,
            },
        ];
    });

    const [activeIndex, setActiveIndex] = useState(0);
    const onActiveChange = useCallback((index: number) => {
        setActiveIndex(index);
    }, []);

    if (slides.length === 0) return null;

    const activeSlide = slides[Math.min(activeIndex, slides.length - 1)]!;
    const activeImage = images.find((img) => img.$id === activeSlide.id);
    const caption = [activeImage?.caption, activeImage?.credit]
        .filter(Boolean)
        .join(' · ');

    return (
        <figure className="m-0">
            <FadeCoverGallery
                slides={slides}
                className="image-frame image-frame--photo aspect-[16/9] w-full border-y border-white/10"
                imageClassName="aspect-[16/9] h-full w-full"
                width={1600}
                height={900}
                fetchPriority="high"
                loading="eager"
                sizes="100vw"
                showArrows
                onActiveChange={onActiveChange}
            />
            {caption && (
                <figcaption className="border-b border-white/10 px-6 py-3 text-sm text-white/55">
                    {caption}
                </figcaption>
            )}
        </figure>
    );
}
